import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthProvider';
import { useTenant } from '@/app/providers/TenantProvider';
import { CommandPalette } from '@/features/search/components/CommandPalette';
import { CopilotPanel } from '@/features/copilot/components/CopilotPanel';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  Home,
  User,
  LogOut,
  Menu,
  X,
  Shield,
  ChevronDown,
  Globe,
  Check,
  LayoutGrid,
  Search,
  Bell,
  Sparkles,
  Users,
  Settings,
  Bot,
  Send,
  Calendar,
  MapPin,
  BarChart2,
} from 'lucide-react';

interface TenantRow {
  id: string;
  slug: string;
  tenant_branding: { app_name: string; candidate_name: string | null } | null;
}

function SuperadminBadge({ compact = false }: { compact?: boolean }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.18em] animate-pulse-glow"
      style={{
        background:
          'linear-gradient(90deg, color-mix(in oklab, var(--color-accent), transparent 80%), color-mix(in oklab, var(--color-accent), transparent 92%))',
        borderColor: 'color-mix(in oklab, var(--color-accent), transparent 55%)',
        color: 'var(--color-accent)',
      }}
    >
      <Shield className="h-2.5 w-2.5" />
      {compact ? 'ADMIN' : 'SUPERADMIN'}
    </span>
  );
}

function TenantSwitcher({
  activeTenantId,
  switchTenant,
}: {
  activeTenantId: string | null;
  switchTenant: (id: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data: tenants = [], isLoading } = useQuery<TenantRow[]>({
    queryKey: ['superadmin', 'tenants-list'],
    queryFn: async (): Promise<TenantRow[]> => {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, slug, tenant_branding(app_name, candidate_name)')
        .eq('active', true)
        .order('slug');
      if (error) throw error;
      if (!data) return [];
      return (data as Record<string, unknown>[]).map((row) => ({
        id: row['id'] as string,
        slug: row['slug'] as string,
        tenant_branding: row['tenant_branding'] as TenantRow['tenant_branding'],
      }));
    },
    staleTime: 60_000,
  });

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const activeTenant = tenants.find((t) => t.id === activeTenantId);
  const displayLabel = activeTenant
    ? activeTenant.tenant_branding?.app_name || activeTenant.slug
    : 'Vista Global';

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-2 rounded-xl px-3 py-2 text-xs font-semibold transition-all glass hover:border-hairline-strong cursor-pointer"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Globe className="h-3.5 w-3.5 text-accent shrink-0" />
          <span className="truncate text-ink">{displayLabel}</span>
        </div>
        <ChevronDown
          className={`h-3.5 w-3.5 text-ink-muted transition-transform duration-200 shrink-0 ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 w-full min-w-[240px] z-50 rounded-2xl glass-strong shadow-deep overflow-hidden animate-fade-in">
          <button
            onClick={() => {
              switchTenant(null);
              setOpen(false);
            }}
            className="flex w-full items-center gap-2.5 border-b border-hairline px-4 py-3 text-xs font-semibold transition-colors cursor-pointer hover:bg-white/5"
            style={{
              color: activeTenantId === null ? 'var(--color-accent)' : 'var(--color-ink-muted)',
              background:
                activeTenantId === null
                  ? 'color-mix(in oklab, var(--color-accent), transparent 92%)'
                  : 'transparent',
            }}
          >
            <Globe className="h-4 w-4" />
            <span className="flex-grow text-left">Vista Global</span>
            {activeTenantId === null && <Check className="h-3.5 w-3.5" />}
          </button>

          <div className="max-h-64 overflow-y-auto">
            {isLoading ? (
              <div className="px-4 py-3 text-xs text-ink-muted">Carregando tenants...</div>
            ) : tenants.length === 0 ? (
              <div className="px-4 py-3 text-xs text-ink-muted">Nenhum tenant ativo</div>
            ) : (
              tenants.map((t) => {
                const label = t.tenant_branding?.app_name || t.slug;
                const sub = t.tenant_branding?.candidate_name;
                const isActive = activeTenantId === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => {
                      switchTenant(t.id);
                      setOpen(false);
                    }}
                    className="flex w-full items-center gap-2.5 px-4 py-3 text-xs transition-colors cursor-pointer hover:bg-white/5"
                    style={{
                      color: isActive ? 'var(--color-accent)' : 'var(--color-ink)',
                      background: isActive
                        ? 'color-mix(in oklab, var(--color-accent), transparent 92%)'
                        : 'transparent',
                    }}
                  >
                    <div
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[10px] font-black"
                      style={{
                        background: 'color-mix(in oklab, var(--color-accent), transparent 85%)',
                        color: 'var(--color-accent)',
                      }}
                    >
                      {label[0].toUpperCase()}
                    </div>
                    <div className="flex-grow overflow-hidden text-left">
                      <p className="truncate font-semibold">{label}</p>
                      {sub && <p className="truncate text-[10px] text-ink-subtle">{sub}</p>}
                    </div>
                    {isActive && <Check className="h-3.5 w-3.5 shrink-0" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function DashboardLayout() {
  const { user, signOut, isSuperAdmin } = useAuth();
  const { branding, role, activeTenantId, switchTenant } = useTenant();
  const location = useLocation();
  const [sidebarOpen,  setSidebarOpen]  = useState(false);
  const [paletteOpen,  setPaletteOpen]  = useState(false);
  const [copilotOpen,  setCopilotOpen]  = useState(false);

  const openPalette   = useCallback(() => setPaletteOpen(true),  []);
  const closePalette  = useCallback(() => setPaletteOpen(false), []);
  const toggleCopilot = useCallback(() => setCopilotOpen((o) => !o), []);
  const closeCopilot  = useCallback(() => setCopilotOpen(false), []);

  // Contexto da página atual para o Copiloto
  const copilotContext = {
    page: location.pathname.startsWith('/app/contatos') ? 'contacts'
        : location.pathname.startsWith('/app/campanhas') ? 'campaigns'
        : location.pathname.startsWith('/app/eventos')   ? 'events'
        : 'dashboard',
  };

  // ⌘K / Ctrl+K → Command Palette  |  ⌘L / Ctrl+L → Lumen Copiloto
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'l') {
        e.preventDefault();
        setCopilotOpen((o) => !o);
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const menuItems = [
    { name: 'Início',         path: '/app',               icon: Home     },
    { name: 'Lumen',          path: '/app/copiloto',      icon: Bot      },
    { name: 'Contatos',       path: '/app/contatos',      icon: Users    },
    { name: 'Campanhas',      path: '/app/campanhas',     icon: Send     },
    { name: 'Agenda',         path: '/app/eventos',       icon: Calendar },
    { name: 'Campo',          path: '/app/campo',         icon: MapPin   },
    { name: 'Relatórios',     path: '/app/relatorios',    icon: BarChart2 },
    { name: 'Configurações',  path: '/app/configuracoes', icon: Settings },
    { name: 'Perfil',         path: '/perfil',            icon: User     },
    ...(isSuperAdmin ? [{ name: 'Painel Admin', path: '/admin', icon: LayoutGrid }] : []),
  ];

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Erro ao sair da conta:', error);
    }
  };

  return (
    <div className="relative min-h-screen bg-midnight text-ink flex flex-col md:flex-row overflow-hidden">
      {/* Aurora ambiente global — visível pelas bordas */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div
          className="aurora-orb aurora-orb-accent animate-aurora-1"
          style={{
            width: 600,
            height: 600,
            top: '-20%',
            left: '-15%',
            opacity: 0.25,
          }}
        />
        <div
          className="aurora-orb aurora-orb-violet animate-aurora-3"
          style={{
            width: 500,
            height: 500,
            bottom: '-20%',
            right: '-15%',
            opacity: 0.2,
          }}
        />
      </div>

      {/* ====== SIDEBAR — force-dark: sempre midnight mesmo em light mode ====== */}
      <aside
        className={`force-dark fixed inset-y-0 left-0 z-40 flex w-72 flex-col justify-between glass-strong border-r border-hairline transform ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } md:static md:translate-x-0 md:h-screen md:shrink-0 transition-transform duration-base ease-cinematic`}
      >
        <div className="flex flex-col gap-7 px-5 pt-7">
          {/* Brand do tenant */}
          <div className="pb-5 hairline-b space-y-2">
            {branding.logo_url ? (
              /* Logo customizado do tenant */
              <div className="flex items-center gap-3">
                <img
                  src={branding.logo_url}
                  alt={branding.app_name}
                  className="h-11 w-11 rounded-xl object-contain shadow-glow-xs shrink-0"
                />
                <div className="min-w-0 overflow-hidden">
                  <h2 className="truncate font-display text-sm font-bold tracking-tight text-white">
                    {branding.app_name}
                  </h2>
                  {branding.candidate_name ? (
                    <p className="truncate text-[10px] font-semibold uppercase tracking-wider text-accent mt-0.5">
                      {branding.candidate_name}{branding.party ? ` · ${branding.party}` : ''}
                    </p>
                  ) : branding.slogan ? (
                    <p className="truncate text-[10px] text-ink-subtle mt-0.5">{branding.slogan}</p>
                  ) : null}
                </div>
              </div>
            ) : (
              /* Wordmark Lumenise (fallback sem logo_url) */
              <>
                <img
                  src="/lumenise-base.png"
                  alt="Lumenise"
                  className="w-40 h-auto object-contain object-left select-none"
                  draggable={false}
                />
                {branding.candidate_name ? (
                  <p className="truncate text-[10px] font-semibold uppercase tracking-wider text-accent">
                    {branding.candidate_name}{branding.party ? ` · ${branding.party}` : ''}
                  </p>
                ) : branding.slogan ? (
                  <p className="truncate text-[10px] text-ink-subtle">{branding.slogan}</p>
                ) : null}
              </>
            )}
          </div>

          {/* Superadmin tenant switcher */}
          {isSuperAdmin && (
            <div className="flex flex-col gap-2">
              <span className="font-mono text-[9px] uppercase tracking-[0.25em] font-semibold text-ink-subtle">
                Contexto ativo
              </span>
              <TenantSwitcher activeTenantId={activeTenantId} switchTenant={switchTenant} />
            </div>
          )}

          {/* Navegação */}
          <nav className="space-y-1">
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] font-semibold text-ink-subtle mb-2 px-1">
              Navegação
            </div>
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive =
                location.pathname === item.path ||
                (item.path !== '/app' && location.pathname.startsWith(item.path));
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`group relative flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-all duration-fast ${
                    isActive
                      ? 'bg-accent/15 text-accent shadow-glow-xs'
                      : 'text-ink-muted hover:bg-white/5 hover:text-ink'
                  }`}
                >
                  {isActive && (
                    <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-r bg-accent shadow-glow-sm" />
                  )}
                  <Icon className="h-4 w-4 shrink-0" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Footer usuário */}
        <div className="space-y-3 p-4 hairline-t">
          <div className="flex items-center gap-3 rounded-xl bg-white/[0.03] hairline p-3">
            <div className="h-9 w-9 shrink-0 rounded-full bg-accent/15 hairline flex items-center justify-center font-bold uppercase text-accent">
              {user?.email?.[0] || 'U'}
            </div>
            <div className="min-w-0 flex flex-col gap-1 overflow-hidden">
              <p className="truncate text-xs font-bold leading-tight text-white">{user?.email}</p>
              {isSuperAdmin ? (
                <SuperadminBadge compact />
              ) : (
                <p className="font-mono text-[10px] capitalize text-ink-subtle">{role}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-danger/20 bg-danger/5 py-2.5 px-4 text-xs font-semibold text-danger transition-colors hover:bg-danger/15 cursor-pointer"
          >
            <LogOut className="h-3.5 w-3.5" />
            <span>Sair da conta</span>
          </button>
        </div>
      </aside>

      {/* Header mobile */}
      <header className="force-dark md:hidden sticky top-0 z-30 flex h-16 items-center gap-2 justify-between px-5 glass-strong hairline-b">
        <div className="flex items-center gap-2.5">
          {branding.logo_url ? (
            <img
              src={branding.logo_url}
              alt={branding.app_name}
              className="h-8 w-8 rounded-lg object-contain"
            />
          ) : (
            <div className="h-8 w-8 rounded-lg bg-accent/20 flex items-center justify-center font-display font-extrabold text-base text-accent">
              {branding.app_name?.[0] || 'L'}
            </div>
          )}
          <span className="font-display text-sm font-bold tracking-tight">
            {branding.app_name}
          </span>
          {isSuperAdmin && <SuperadminBadge compact />}
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={openPalette}
            className="rounded-lg p-2 text-ink-muted hover:bg-white/5 hover:text-ink transition-colors cursor-pointer"
            title="Busca global (⌘K)"
          >
            <Search className="h-5 w-5" />
          </button>
          <button
            onClick={toggleCopilot}
            title="Lumen AI"
            className={`relative rounded-lg p-2 transition-colors cursor-pointer ${
              copilotOpen ? 'text-accent bg-accent/10' : 'text-ink-muted hover:bg-white/5 hover:text-ink'
            }`}
          >
            <Bot className="h-5 w-5" />
            {!copilotOpen && (
              <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
            )}
          </button>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="rounded-lg p-2 text-ink-muted hover:bg-white/5 hover:text-ink transition-colors cursor-pointer"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      {/* Backdrop mobile */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm md:hidden"
        />
      )}

      {/* ====== ÁREA PRINCIPAL ====== */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Topbar desktop */}
        <div className="hidden md:flex h-16 items-center justify-between px-8 hairline-b sticky top-0 z-20 glass">
          <button
            onClick={openPalette}
            className="group relative flex w-full max-w-md items-center gap-3 rounded-xl bg-white/[0.03] hairline pl-4 pr-4 py-2 text-sm text-ink-faint hover:bg-white/[0.06] hover:border-accent/30 transition-all cursor-text"
          >
            <Search className="h-4 w-4 shrink-0 text-ink-subtle group-hover:text-ink transition-colors" />
            <span className="flex-1 text-left">Buscar contatos, campanhas, eventos…</span>
            <kbd className="font-mono text-[10px] text-ink-faint border border-hairline rounded px-1.5 py-0.5 shrink-0">
              ⌘K
            </kbd>
          </button>

          <div className="flex items-center gap-3">
            {/* Botão Lumen — abre painel lateral (⌘L) */}
            <button
              onClick={toggleCopilot}
              title="Lumen AI (⌘L)"
              className={`group relative rounded-xl p-2 transition-colors cursor-pointer ${
                copilotOpen
                  ? 'bg-accent/15 text-accent'
                  : 'text-ink-muted hover:bg-white/5 hover:text-ink'
              }`}
            >
              <Sparkles className="h-4 w-4" />
              {!copilotOpen && (
                <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full bg-accent animate-pulse shadow-glow-xs" />
              )}
            </button>
            <button className="group relative rounded-xl p-2 text-ink-muted hover:bg-white/5 hover:text-ink transition-colors cursor-pointer">
              <Bell className="h-4 w-4" />
              <span className="absolute top-1.5 right-1.5 h-1.5 w-1.5 rounded-full bg-danger" />
            </button>
          </div>
        </div>

        {/* Conteúdo */}
        <main className="flex-grow overflow-x-hidden p-6 md:p-10 lg:p-12 max-w-[1600px] w-full animate-fade-in">
          <Outlet />
        </main>
      </div>

      {/* Command palette — portal over everything */}
      <CommandPalette open={paletteOpen} onClose={closePalette} />

      {/* Lumen AI — painel lateral deslizante via portal */}
      {createPortal(
        <>
          {/* Backdrop */}
          {copilotOpen && (
            <div
              className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm animate-fade-in md:hidden"
              onClick={closeCopilot}
            />
          )}

          {/* Painel lateral */}
          <div
            className={`fixed inset-y-0 right-0 z-50 flex w-full sm:w-[420px] flex-col transform transition-transform duration-300 ease-out ${
              copilotOpen ? 'translate-x-0' : 'translate-x-full'
            }`}
          >
            {copilotOpen && (
              <CopilotPanel
                compact
                onClose={closeCopilot}
                context={copilotContext}
              />
            )}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
